import React, { useState, useRef } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../service/IdApi";
import PageLoading from "../../../components/PageLoading";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { Column } from "primereact/column";
import { Button } from 'primereact/button';
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";
import { useUnclaimedProfileSwitchDialog } from "../../../hooks/useUnclaimedProfileSwitchDialog";
import { throttle } from "lodash";

const filterOptions = [
    { name: 'Company Name', code: 'companyName' },
    { name: 'Provider Number', code: 'providerNumber' }
];

const AllProviders = () => {
    const { switchUnclaimedProfile, UnclaimedProfileSwitchDialog } = useUnclaimedProfileSwitchDialog();
    const [providers, setProviders] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [filterByFields, setFilterByFields] = useState([{ name: 'Company Name', code: 'companyName' }]);
    const [nameFilter, setNameFilter] = useState("");
    const [numberFilter, setNumberFilter] = useState("");

    const [lazyState, setlazyState] = useState({
        first: 0,
        rows: 50,
        page: 1,
        sortField: "createdAt",
        sortOrder: -1,
    });

    let params = "?first=" + lazyState.first + "&rows=" + lazyState.rows;
    if (lazyState.sortField) {
        params += "&sortField=" + lazyState.sortField;
        if (lazyState.sortOrder) {
            params += "&sortOrder=" + lazyState.sortOrder;
        }
    }

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["Providers", params], async () => {
        var searchBody = {};
        searchBody.first = lazyState.first;
        searchBody.rows = lazyState.rows;
        searchBody.sortField = lazyState.sortField;
        searchBody.sortOrder = lazyState.sortOrder;
        if (filterByFields.some(filter => filter.code === "providerNumber") && numberFilter !== "") {
            const _number = Number(numberFilter);
            if (!isNaN(_number)) {
                searchBody.providerNumber = _number;
            }
        }
        if (filterByFields.some(filter => filter.code === "companyName") && nameFilter !== "") {
            searchBody.name = nameFilter;
        }
        const result = await authApi.post("admin/provider/list", searchBody);
        setProviders(result.data.providers);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
        return data;
    });

    const pageHeader = (
        <PageHeading pageTitle="Admin - All Providers">
            <div className="block">
            </div>
        </PageHeading>
    );

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    if (!loaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    const onPage = (event) => {
        setlazyState(event);
    };

    const onSort = (event) => {
        setlazyState(event);
    };

    const onRowSelect = (event) => {
        // if (event.type !== "checkbox") {
        //     navigate("edit/" + event.data.itemNumber);
        // }
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const nameClicked = throttle(async (e, row) => {
        e.preventDefault();
        switchUnclaimedProfile(row._id);
    }, 500, { trailing: false });

    const nameBodyTemplate = (rowData) => {
        return (
            <div className="">
                <a href="#" onClick={(e) => nameClicked(e, rowData)}>{rowData.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} /></a>
            </div>
        );
    };

    const handleSearch = () => {
        refetch();
    };

    const clearFilters = () => {
        setFilterByFields([]);
        setNameFilter("");
        setNumberFilter("");
        setTimeout(() => {
            refetch();
        }, 100);
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div>
            {pageHeader}
            {UnclaimedProfileSwitchDialog}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row flex-wrap align-items-center">
                            <div className="font-bold mr-3 mb-2">
                                Filter By
                            </div>
                            <div className="flex mr-3 mb-2">
                                <MultiSelect value={filterByFields} onChange={(e) => setFilterByFields(e.value)} options={filterOptions} optionLabel="name"
                                    maxSelectedLabels={3} />
                            </div>
                            {filterByFields.some(filter => filter.code === "companyName") && (
                                <div className="flex mr-3 mb-2">
                                    <InputText
                                        value={nameFilter}
                                        onChange={(e) => setNameFilter(e.target.value)}
                                        placeholder="Company Name"
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            )}
                            {filterByFields.some(filter => filter.code === "providerNumber") && (
                                <div className="flex mr-3 mb-2">
                                    <InputText
                                        value={numberFilter}
                                        onChange={(e) => setNumberFilter(e.target.value)}
                                        placeholder="Provider Number"
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                            )}
                            {filterByFields.length > 0 && (
                                <>
                                    <div className="flex mb-2">
                                        <Button
                                            icon="pi pi-search"
                                            className="p-button-primary"
                                            onClick={handleSearch}
                                            tooltip="Search"
                                            loading={isLoading || isFetching}
                                            label="Search"
                                        />
                                    </div>
                                    <div className="flex mb-2">
                                        <Button onClick={clearFilters} className="p-button-text" icon="pi pi-times" tooltip="Clear Filter" />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Button type="button" icon="pi pi-file-export" rounded onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <DataTable
                        ref={dataTable}
                        value={providers}
                        // selection={selectedItems}
                        dataKey="_id"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[25, 50, 100, 500]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} providers"
                        emptyMessage="No providers found."
                        onRowSelect={onRowSelect}
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || !loaded}
                    >
                        <Column body={nameBodyTemplate} field="name" header="Name" sortable />
                        <Column field="providerNumber" header="Provider Number" sortable />
                        <Column field="_id" header="ID" />
                        <Column field="createdAt" header="Created At" sortable />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};

export default AllProviders;
